import * as THREE from "three";

/** Pointer distance, in screen pixels, within which a corner takes over. */
export const RULER_3D_SNAP_PIXELS = 24;

/**
 * Adjacent surfaces often overlap at a visible floor/wall or wall/wall
 * junction. Consider hits just behind the front surface so those shared
 * features remain available instead of being masked by whichever mesh the
 * raycaster happened to return first.
 */
const RULER_3D_ADJACENT_HIT_DEPTH = 0.75;
const RULER_3D_ADJACENT_HIT_DEPTH_RATIO = 0.025;

const FLOOR_PLANE = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

/** Overlays and helper surfaces a measurement must never latch onto. */
export const RULER_3D_EXCLUDED_HIT_TYPES = new Set([
  "measurement-3d-layer",
  "measurement-3d",
  "ruler-3d-construction-preview",
  "base-plane-pan-hit-target",
  "floor-edge",
  "wall-edge",
  "ceiling-edge",
]);

export type Ruler3DSnapIntersection = {
  object: THREE.Object3D;
  point: THREE.Vector3;
  face?: THREE.Face | null;
  distance?: number;
};

export type Ruler3DSnapResult = {
  point: THREE.Vector3;
  snapped: boolean;
  kind: "feature" | "surface" | "none";
};

export type Ruler3DTranslationResult = {
  start: THREE.Vector3;
  end: THREE.Vector3;
  snapped: boolean;
  kind: Ruler3DSnapResult["kind"];
};

const LOCAL_BOX_OFFSETS: [number, number, number][] = (() => {
  const offsets: [number, number, number][] = [];
  [-1, 0, 1].forEach((x) => {
    [-1, 0, 1].forEach((y) => {
      [-1, 0, 1].forEach((z) => {
        if (x === 0 && y === 0 && z === 0) return;
        offsets.push([x, y, z]);
      });
    });
  });
  return offsets;
})();

function getMeshGeometry(object: THREE.Object3D): THREE.BufferGeometry | null {
  const geometry = (object as THREE.Mesh).geometry;
  return geometry instanceof THREE.BufferGeometry ? geometry : null;
}

/** World-space vertices of the triangle under the pointer, if there is one. */
function collectFaceVertices(
  object: THREE.Object3D,
  face: THREE.Face | null | undefined,
): THREE.Vector3[] {
  if (!face) return [];
  const geometry = getMeshGeometry(object);
  const position = geometry?.attributes.position;
  if (!position) return [];

  return [face.a, face.b, face.c]
    .filter((index) => index >= 0 && index < position.count)
    .map((index) =>
      new THREE.Vector3()
        .fromBufferAttribute(position as THREE.BufferAttribute, index)
        .applyMatrix4(object.matrixWorld),
    );
}

/**
 * Corners, edge midpoints and face centers of the object's own (unrotated)
 * bounding box, mapped into world space. Using the local box keeps the points
 * on the object's real corners even when the symbol is rotated in plan.
 */
function collectOrientedBoxPoints(object: THREE.Object3D): THREE.Vector3[] {
  const geometry = getMeshGeometry(object);
  if (!geometry) return [];
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return [];

  const center = box.getCenter(new THREE.Vector3());
  const half = box.getSize(new THREE.Vector3()).multiplyScalar(0.5);

  return LOCAL_BOX_OFFSETS.map(([x, y, z]) =>
    new THREE.Vector3(
      center.x + half.x * x,
      center.y + half.y * y,
      center.z + half.z * z,
    ).applyMatrix4(object.matrixWorld),
  );
}

/**
 * Every point a 3D measurement is allowed to latch onto for the given hit:
 * the corners of the picked triangle plus the object's box corners, edge
 * midpoints and face centers.
 */
export function getRuler3DSnapCandidates(
  intersection: Ruler3DSnapIntersection,
): THREE.Vector3[] {
  return [
    ...collectFaceVertices(intersection.object, intersection.face),
    ...collectOrientedBoxPoints(intersection.object),
  ];
}

/**
 * Picks the candidate closest to the pointer in screen space. Working in screen
 * space keeps the snap radius constant no matter how far away the object is.
 */
export function findClosestScreenSpacePoint(
  candidates: THREE.Vector3[],
  pointerNdc: THREE.Vector2,
  camera: THREE.Camera,
  toleranceNdc: number,
): THREE.Vector3 | null {
  if (!(toleranceNdc > 0)) return null;

  let best: THREE.Vector3 | null = null;
  let bestDistance = Infinity;
  const projected = new THREE.Vector3();

  candidates.forEach((candidate) => {
    projected.copy(candidate).project(camera);
    if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) return;
    const distance = Math.hypot(
      projected.x - pointerNdc.x,
      projected.y - pointerNdc.y,
    );
    if (distance > toleranceNdc || distance >= bestDistance) return;
    bestDistance = distance;
    best = candidate;
  });

  return best ? (best as THREE.Vector3).clone() : null;
}

/** Converts a pixel radius into normalized device coordinates. */
export function pixelsToNdcTolerance(
  pixels: number,
  elementWidth: number,
  elementHeight: number,
): number {
  const shortestSide = Math.min(elementWidth, elementHeight);
  if (!(shortestSide > 0)) return 0;
  return (pixels / shortestSide) * 2;
}

/**
 * Snaps a raw surface hit onto the nearest feature of the object under the
 * pointer. When no feature is close enough, the raw surface remains a valid
 * snap target so endpoints can adhere anywhere along a wall or floor.
 */
export function snapRuler3DPointToGeometry(
  intersection: Ruler3DSnapIntersection | null,
  pointerNdc: THREE.Vector2,
  camera: THREE.Camera,
  elementWidth: number,
  elementHeight: number,
  snapPixels = RULER_3D_SNAP_PIXELS,
): Ruler3DSnapResult | null {
  if (!intersection) return null;

  const tolerance = pixelsToNdcTolerance(
    snapPixels,
    elementWidth,
    elementHeight,
  );
  const snapped = findClosestScreenSpacePoint(
    getRuler3DSnapCandidates(intersection),
    pointerNdc,
    camera,
    tolerance,
  );

  return snapped
    ? { point: snapped, snapped: true, kind: "feature" }
    : { point: intersection.point.clone(), snapped: true, kind: "surface" };
}

export function getRuler3DHitType(object: THREE.Object3D): string | null {
  let current: THREE.Object3D | null = object;
  while (current) {
    const hitType = current.userData?.floorplannerType;
    if (typeof hitType === "string") return hitType;
    current = current.parent;
  }
  return null;
}

export function isRuler3DPickableHit(object: THREE.Object3D): boolean {
  const hitType = getRuler3DHitType(object);
  return Boolean(hitType && !RULER_3D_EXCLUDED_HIT_TYPES.has(hitType));
}

export function objectAndAncestorsVisible(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (!current.visible) return false;
    current = current.parent;
  }
  return true;
}

export function getRuler3DPickableIntersection(
  intersections: Ruler3DSnapIntersection[],
): Ruler3DSnapIntersection | null {
  return getRuler3DPickableIntersections(intersections)[0] ?? null;
}

export function getRuler3DPickableIntersections(
  intersections: Ruler3DSnapIntersection[],
): Ruler3DSnapIntersection[] {
  return intersections.filter(({ object, point }) => {
    if (
      !Number.isFinite(point.x) ||
      !Number.isFinite(point.y) ||
      !Number.isFinite(point.z)
    ) {
      return false;
    }
    return isRuler3DPickableHit(object);
  });
}

/**
 * Resolves a snap across the front surface and any immediately adjacent
 * surfaces. Feature points win; otherwise the pointer adheres to the raw front
 * surface, which enables measurements at arbitrary heights along a wall.
 */
export function snapRuler3DPointToIntersections(
  intersections: Ruler3DSnapIntersection[],
  pointerNdc: THREE.Vector2,
  camera: THREE.Camera,
  elementWidth: number,
  elementHeight: number,
  snapPixels = RULER_3D_SNAP_PIXELS,
): Ruler3DSnapResult | null {
  const frontHit = intersections[0];
  if (!frontHit) return null;

  const tolerance = pixelsToNdcTolerance(
    snapPixels,
    elementWidth,
    elementHeight,
  );
  const nearbyHits = getAdjacentRuler3DHits(intersections);
  const snapped = findClosestScreenSpacePoint(
    nearbyHits.flatMap(getRuler3DSnapCandidates),
    pointerNdc,
    camera,
    tolerance,
  );

  return snapped
    ? { point: snapped, snapped: true, kind: "feature" }
    : { point: frontHit.point.clone(), snapped: true, kind: "surface" };
}

/**
 * Applies the best endpoint snap as a pure translation, preserving the ruler's
 * length and direction while the whole stick is dragged.
 */
export function snapRuler3DTranslation(
  start: THREE.Vector3,
  end: THREE.Vector3,
  startSnap: Ruler3DSnapResult | null,
  endSnap: Ruler3DSnapResult | null,
): Ruler3DTranslationResult {
  const candidates = [
    startSnap?.snapped
      ? {
          snap: startSnap,
          correction: new THREE.Vector3().subVectors(startSnap.point, start),
        }
      : null,
    endSnap?.snapped
      ? {
          snap: endSnap,
          correction: new THREE.Vector3().subVectors(endSnap.point, end),
        }
      : null,
  ].filter(
    (
      candidate,
    ): candidate is {
      snap: Ruler3DSnapResult;
      correction: THREE.Vector3;
    } =>
      Boolean(
        candidate &&
          Number.isFinite(candidate.correction.x) &&
          Number.isFinite(candidate.correction.y) &&
          Number.isFinite(candidate.correction.z),
      ),
  );

  candidates.sort((a, b) => {
    const kindDelta =
      snapKindPriority(a.snap.kind) - snapKindPriority(b.snap.kind);
    if (kindDelta !== 0) return kindDelta;
    return a.correction.lengthSq() - b.correction.lengthSq();
  });

  const winner = candidates[0];
  if (!winner) {
    return {
      start: start.clone(),
      end: end.clone(),
      snapped: false,
      kind: "none",
    };
  }

  return {
    start: start.clone().add(winner.correction),
    end: end.clone().add(winner.correction),
    snapped: true,
    kind: winner.snap.kind,
  };
}

/**
 * Raycasts the scene under the pointer and returns a geometry-snapped placement
 * point for a 3D measurement, falling back to the floor plane when nothing
 * pickable is under the cursor.
 */
export function resolveRuler3DPlacement(
  event: { clientX: number; clientY: number },
  camera: THREE.Camera,
  element: HTMLElement,
  scene: THREE.Object3D,
  snapPixels = RULER_3D_SNAP_PIXELS,
): Ruler3DSnapResult | null {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const pointer = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(pointer, camera);

  const fallbackPoint = raycaster.ray.intersectPlane(
    FLOOR_PLANE,
    new THREE.Vector3(),
  );
  const intersections = raycaster
    .intersectObjects(scene.children, true)
    .filter((intersection) => objectAndAncestorsVisible(intersection.object));
  const hits = getRuler3DPickableIntersections(intersections);

  // Features from touching surfaces win over the raw front surface, so a floor
  // cannot mask the bottom of a wall and one wall cannot mask a meeting wall.
  const snap = snapRuler3DPointToIntersections(
    hits,
    pointer,
    camera,
    rect.width,
    rect.height,
    snapPixels,
  );
  if (snap) return snap;

  return fallbackPoint
    ? { point: fallbackPoint.clone(), snapped: false, kind: "none" }
    : null;
}

function getAdjacentRuler3DHits(
  intersections: Ruler3DSnapIntersection[],
): Ruler3DSnapIntersection[] {
  const frontHit = intersections[0];
  if (!frontHit || !Number.isFinite(frontHit.distance)) {
    return frontHit ? [frontHit] : [];
  }

  const depthTolerance = Math.max(
    RULER_3D_ADJACENT_HIT_DEPTH,
    (frontHit.distance as number) * RULER_3D_ADJACENT_HIT_DEPTH_RATIO,
  );

  return intersections.filter(
    (intersection) =>
      !Number.isFinite(intersection.distance) ||
      (intersection.distance as number) <=
        (frontHit.distance as number) + depthTolerance,
  );
}

function snapKindPriority(kind: Ruler3DSnapResult["kind"]): number {
  if (kind === "feature") return 0;
  if (kind === "surface") return 1;
  return 2;
}
